import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Facebook } from 'lucide-react';
import { contactInfo, navigation } from '../data';
import { cn } from '../lib/utils';
import Logo from './Logo';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b py-3',
        isScrolled 
          ? 'bg-[#004e3a] border-[#003d2e] shadow-md' 
          : 'bg-[#004e3a] border-[#003d2e]'
      )}
    >
      <div className="container mx-auto px-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Logo variant="white" className="h-[80px] w-auto" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden min-[1200px]:flex items-center gap-8">
          {navigation.map((item) => (
            <div key={item.name} className="relative group">
              <NavLink
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'text-sm font-medium transition-colors py-2',
                    isActive ? 'text-emerald-400' : 'text-white hover:text-emerald-400'
                  )
                }
              >
                {item.name}
              </NavLink>
              {item.children && (
                <div className="absolute top-full left-0 mt-2 bg-white shadow-xl rounded-none py-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0 border border-stone-100 w-72 flex flex-col">
                  {item.children.map((child) => (
                    <NavLink
                      key={child.name}
                      to={child.href}
                      className={({ isActive }) =>
                        cn(
                          'block px-6 py-2 text-sm transition-colors',
                          isActive 
                            ? 'bg-emerald-50 text-emerald-900 font-semibold' 
                            : 'text-stone-700 hover:bg-emerald-50 hover:text-emerald-900'
                        )
                      }
                    >
                      {child.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
          <a
            href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
            className="flex items-center gap-2 px-5 py-2.5 rounded-none text-sm font-semibold transition-all bg-emerald-600 text-white hover:bg-emerald-500"
          >
            <Phone className="w-4 h-4" />
            {contactInfo.phone}
          </a>
        </nav>

        {/* Mobile Toggle */}
        <button
          className="min-[1200px]:hidden p-2 rounded-none transition-colors text-white"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Nav */}
      <div
        className={cn(
          'fixed inset-0 bg-[#004e3a] z-40 min-[1200px]:hidden transition-transform duration-300 pt-24 px-6 overflow-y-auto',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Close Button */}
        <button
          className="absolute top-6 right-4 p-2 text-white hover:text-emerald-400 transition-colors"
          onClick={() => setIsOpen(false)}
          aria-label="Luk menu"
        >
          <X className="w-8 h-8" />
        </button>

        <div className="flex flex-col gap-6 pb-12">
          {navigation.map((item) => (
            <div key={item.name} className="flex flex-col gap-3">
              <NavLink
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'text-base font-semibold transition-colors',
                    isActive ? 'text-emerald-400' : 'text-white hover:text-emerald-400'
                  )
                }
                onClick={() => setIsOpen(false)}
              >
                {item.name}
              </NavLink>
              {item.children && (
                <div className="flex flex-col gap-2 pl-4 border-l border-emerald-800">
                  {item.children.map((child) => (
                    <NavLink
                      key={child.name}
                      to={child.href}
                      className={({ isActive }) =>
                        cn(
                          'text-sm transition-colors',
                          isActive ? 'text-emerald-400 font-medium' : 'text-emerald-100/70 hover:text-white'
                        )
                      }
                      onClick={() => setIsOpen(false)}
                    >
                      {child.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="mt-8 pt-8 border-t border-emerald-800 flex flex-col gap-4">
            <a
              href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
              className="flex items-center gap-3 text-base font-medium text-white"
            >
              <Phone className="w-5 h-5 text-emerald-400" />
              {contactInfo.phone}
            </a>
            <a
              href={contactInfo.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-base font-medium text-white"
            >
              <Facebook className="w-5 h-5 text-emerald-400" />
              Følg os på Facebook
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
